// Translatable-copy factories for DS composites used by the campaigns
// feature (CampaignRow, CadencePreview). Keeps i18n calls collocated with
// the UI surface that consumes them.

import type { ReactNode } from 'react';

import type { CampaignRowCopy } from '@barel/ui';
import type { CadencePreviewCopy } from '@barel/ui';

import type { useT } from '@barel/i18n';

type T = ReturnType<typeof useT>;

export function buildCampaignRowCopy(t: T): CampaignRowCopy {
  return {
    statusLabel: {
      draft: t('status.draft'),
      active: t('status.active'),
      paused: t('status.paused'),
      paused_auto: t('status.paused_auto'),
      finished: t('status.finished'),
      error: t('status.error'),
    },
    stats: {
      leads: t('list.stats.leads'),
      invited: t('list.stats.invited'),
      accepted: t('list.stats.accepted'),
      replied: t('list.stats.replied'),
      hot: t('list.stats.hot'),
    },
    progressDescription: ({ pct, daysRunning, dailyLimit }) => {
      const pctCopy = t('list.progressDescription', { pct });
      if (daysRunning > 0) {
        return `${pctCopy as string} · ${
          t('list.progressDays', {
            days: daysRunning,
            dailyLimit,
          }) as string
        }`;
      }
      return pctCopy as ReactNode;
    },
    draftHint: t('list.draftHint'),
    kebabLabel: t('list.kebabLabel') as string,
  };
}

export function buildCadenceCopy(t: T): CadencePreviewCopy {
  return {
    steps: {
      invit: {
        label: t('cadencePreview.invit.label'),
        description: t('cadencePreview.invit.withMsg'),
        icon: '👋',
      },
      fu1: {
        label: t('cadencePreview.fu1.label'),
        description: t('cadencePreview.fu1.desc'),
        icon: '💬',
      },
      fu2: {
        label: t('cadencePreview.fu2.label'),
        description: t('cadencePreview.fu2.desc'),
        icon: '💡',
      },
      fu3: {
        label: t('cadencePreview.fu3.label'),
        description: t('cadencePreview.fu3.desc'),
        icon: '🎯',
      },
    },
    invitDayLabel: t('cadencePreview.j0'),
    fuDayLabel: (days) => t('cadencePreview.jPlus', { days }) as string,
    smartLimitLabel: (dailyLimit) => t('detail.smartLimit', { dailyLimit }) as string,
    ariaLabel: t('detail.cadenceAria') as string,
  };
}
